import { createApi } from '@reduxjs/toolkit/query/react';
import { graphqlRequestBaseQuery } from '@rtk-query/graphql-request-base-query';
import { client } from 'graphql/client';
import { createTeam, updateTeam } from 'graphql/mutations';
import { getSystemTeamInfo, listSystemTeams } from 'graphql/queries';
import { z } from 'zod';

import { errorHandler } from '../errors';

import type { CreateTeamMutationVariables, SystemTeam, SystemTeamInfo, Team, UpdateTeamMutationVariables } from 'API';
import { SystemTeamInfoSchema, SystemTeamSchema } from 'APIzod';

const listSystemTeamsQueryFn = async () => {
    try {
        const { data } = await client.graphql({
            query: listSystemTeams,
        });
        return { data: z.array(SystemTeamSchema()).parse(data.listSystemTeams) };
    } catch (error) {
        throw errorHandler('Unexpected error occurred while fetching system teams', error);
    }
};

const getSystemTeamAttributeQueryFn = async (id: string) => {
    try {
        const { data } = await client.graphql({ query: getSystemTeamInfo, variables: { id } });
        return { data: SystemTeamInfoSchema().parse(data.getSystemTeamInfo) };
    } catch (error) {
        throw errorHandler(`Unexpected error occurred while fetching system team ${id} attributes`, error);
    }
};

const createTeamFn = async (variables: CreateTeamMutationVariables) => {
    try {
        const { data } = await client.graphql({ query: createTeam, variables });
        return {
            data: data.createTeam,
        };
    } catch (error) {
        throw errorHandler('Unexpected error occurred while creating team', error);
    }
};

const editTeamFn = async (variables: UpdateTeamMutationVariables) => {
    try {
        const { data } = await client.graphql({ query: updateTeam, variables });
        return {
            data: data.updateTeam,
        };
    } catch (error) {
        throw errorHandler('Unexpected error occurred while creating user', error);
    }
};

export const systemTeamsApi = createApi({
    reducerPath: 'systemTeams',
    tagTypes: ['Teams'],
    baseQuery: graphqlRequestBaseQuery({
        url: '/graphql',
    }),
    refetchOnMountOrArgChange: true,
    endpoints: builder => ({
        createTeam: builder.mutation<Team, CreateTeamMutationVariables>({
            queryFn: createTeamFn,
            invalidatesTags: ['Teams'],
        }),
        editTeam: builder.mutation<Team, UpdateTeamMutationVariables>({
            queryFn: editTeamFn,
            invalidatesTags: ['Teams'],
        }),
        listSystemTeams: builder.query<SystemTeam[], string>({
            queryFn: listSystemTeamsQueryFn,
            providesTags: ['Teams'],
        }),
        getSystemTeamsAttributes: builder.query<SystemTeamInfo, string>({
            queryFn: getSystemTeamAttributeQueryFn,
        }),
    }),
});

export const {
    useListSystemTeamsQuery,
    useGetSystemTeamsAttributesQuery,
    useLazyListSystemTeamsQuery,
    useCreateTeamMutation,
    useEditTeamMutation,
} = systemTeamsApi;
